import React, { useEffect, useMemo, useRef, useState } from "react";

import CustomSelect from "../DesignSystem/Dropdowns/CustomSelect";
import { useStaffHolidayTracker } from "./hooks/useStaffHolidayTracker";
import { getLeaveCodeForTracker } from "./utils/staffHolidayFormatters";
import { getCachedBankHolidays } from "./services/staffHolidayBankHolidayService";
import { useUserPermissions } from "../SignIn/UserPermissionsContext";
import { hasHolidayAdminPermission } from "./utils/staffHolidayPermissions";

const getMonthDates = (year, monthIndex) => {
  const dates = [];
  const current = new Date(year, monthIndex, 1);

  while (current.getMonth() === monthIndex) {
    const day = current.getDay();

    if (day !== 0 && day !== 6) {
      dates.push(new Date(current));
    }

    current.setDate(current.getDate() + 1);
  }

  return dates;
};

const toDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const normaliseEmail = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase();

const getDateKeyFromValue = (value) => {
  if (!value) return "";

  const timestampDate = value?.toDate?.();

  if (timestampDate instanceof Date && !Number.isNaN(timestampDate.getTime())) {
    return toDateKey(timestampDate);
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return toDateKey(value);
  }

  const rawValue = String(value).trim();

  // YYYY-MM-DD
  const isoMatch = rawValue.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);

  if (isoMatch) {
    const year = isoMatch[1];
    const month = String(isoMatch[2]).padStart(2, "0");
    const day = String(isoMatch[3]).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  // DD/MM/YYYY
  const ukMatch = rawValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);

  if (ukMatch) {
    const year = ukMatch[3];
    const month = String(ukMatch[2]).padStart(2, "0");
    const day = String(ukMatch[1]).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  return "";
};

const isHalfDayType = (value = "") => {
  const normalisedValue = String(value || "")
    .trim()
    .toLowerCase();

  return ["morning", "afternoon", "half", "half_day", "half-day"].includes(
    normalisedValue,
  );
};

const getYearAndMonth = (value) => {
  if (!value) return null;

  const timestampDate = value?.toDate?.();

  if (timestampDate instanceof Date && !Number.isNaN(timestampDate.getTime())) {
    return {
      year: timestampDate.getFullYear(),
      monthIndex: timestampDate.getMonth(),
    };
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return {
      year: value.getFullYear(),
      monthIndex: value.getMonth(),
    };
  }

  const rawValue = String(value).trim();

  // Supports YYYY-MM-DD
  const isoMatch = rawValue.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);

  if (isoMatch) {
    const year = Number(isoMatch[1]);
    const monthIndex = Number(isoMatch[2]) - 1;

    if (monthIndex >= 0 && monthIndex <= 11) {
      return {
        year,
        monthIndex,
      };
    }
  }

  // Supports DD/MM/YYYY
  const ukMatch = rawValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);

  if (ukMatch) {
    const year = Number(ukMatch[3]);
    const monthIndex = Number(ukMatch[2]) - 1;

    if (monthIndex >= 0 && monthIndex <= 11) {
      return {
        year,
        monthIndex,
      };
    }
  }

  return null;
};

const normaliseDepartmentName = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase();

const StaffHolidayTrackerPanel = ({
  focusDate = "",
  initialYear = new Date().getFullYear(),
  initialMonthIndex = new Date().getMonth(),
  departmentId = "all",
  departmentName = "",
  showDepartmentFilter = false,
  compact = false,
  onYearChange,

  highlightStartDate = "",
  highlightEndDate = "",
  highlightStaffEmail = "",
  highlightLeaveType = "",
  highlightStartDayType = "",
  highlightEndDayType = "",
}) => {
  const { userPermissions = [] } = useUserPermissions();

  const isHolidayAdmin = useMemo(
    () => hasHolidayAdminPermission(userPermissions),
    [userPermissions],
  );

  const [year, setYear] = useState(Number(initialYear));
  const [monthIndex, setMonthIndex] = useState(Number(initialMonthIndex));
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(
    String(departmentId || "all"),
  );
  const [bankHolidays, setBankHolidays] = useState([]);

  const staffHeaderRowRef = useRef(null);
  const daysHeaderRowRef = useRef(null);
  const staffBodyRef = useRef(null);
  const daysBodyRef = useRef(null);

  useEffect(() => {
    setYear(Number(initialYear));
  }, [initialYear]);

  useEffect(() => {
    onYearChange?.(year);
  }, [onYearChange, year]);

  useEffect(() => {
    setMonthIndex(Number(initialMonthIndex));
  }, [initialMonthIndex]);

  useEffect(() => {
    setSelectedDepartmentId(String(departmentId || "all"));
  }, [departmentId]);

  useEffect(() => {
    if (!focusDate) return;

    const dateParts = getYearAndMonth(focusDate);

    if (!dateParts) return;

    setYear(dateParts.year);
    setMonthIndex(dateParts.monthIndex);
  }, [focusDate]);

  const trackerDepartmentId = selectedDepartmentId || "all";

  const hasDepartmentName = Boolean(normaliseDepartmentName(departmentName));

  // In the approvals split view, fetch all rows and filter by the
  // request's department name. This avoids mismatched department IDs.
  const trackerQueryDepartmentId = hasDepartmentName
    ? "all"
    : trackerDepartmentId;

  const { staffRows, departments, loading } = useStaffHolidayTracker({
    year,
    departmentId: trackerQueryDepartmentId,
  });

  const visibleStaffRows = useMemo(() => {
    if (!hasDepartmentName) {
      return staffRows;
    }

    const requestedDepartmentName = normaliseDepartmentName(departmentName);

    return staffRows.filter((row) => {
      const rowDepartmentName = normaliseDepartmentName(
        row.departmentName || "No Department",
      );

      return rowDepartmentName === requestedDepartmentName;
    });
  }, [staffRows, departmentName, hasDepartmentName]);

  const highlightedRequestRange = useMemo(() => {
    const startDateKey = getDateKeyFromValue(highlightStartDate);
    const endDateKey = getDateKeyFromValue(highlightEndDate) || startDateKey;

    if (!startDateKey || !endDateKey) {
      return null;
    }

    if (startDateKey <= endDateKey) {
      return {
        startDateKey,
        endDateKey,
      };
    }

    return {
      startDateKey: endDateKey,
      endDateKey: startDateKey,
    };
  }, [highlightStartDate, highlightEndDate]);

  const highlightedStaffEmail = useMemo(
    () => normaliseEmail(highlightStaffEmail),
    [highlightStaffEmail],
  );

  const isHighlightedRequestDate = (dateKey) => {
    if (!highlightedRequestRange) return false;

    return (
      dateKey >= highlightedRequestRange.startDateKey &&
      dateKey <= highlightedRequestRange.endDateKey
    );
  };

  const getHighlightedDayValue = (dateKey) => {
    if (!highlightedRequestRange) return 1;

    const isStartDate = dateKey === highlightedRequestRange.startDateKey;
    const isEndDate = dateKey === highlightedRequestRange.endDateKey;

    if (isStartDate && isHalfDayType(highlightStartDayType)) {
      return 0.5;
    }

    if (isEndDate && isHalfDayType(highlightEndDayType)) {
      return 0.5;
    }

    return 1;
  };

  const monthDates = useMemo(
    () => getMonthDates(Number(year), Number(monthIndex)),
    [year, monthIndex],
  );

  const todayDateKey = useMemo(() => toDateKey(new Date()), []);

  useEffect(() => {
    let cancelled = false;

    const loadBankHolidays = async () => {
      try {
        const dates = await getCachedBankHolidays({
          region: "england-and-wales",
          year: Number(year),
        });

        if (!cancelled) {
          setBankHolidays(Array.isArray(dates) ? dates : []);
        }
      } catch (error) {
        if (!cancelled) {
          setBankHolidays([]);
        }
      }
    };

    loadBankHolidays();

    return () => {
      cancelled = true;
    };
  }, [year]);

  const bankHolidayDateKeys = useMemo(
    () =>
      new Set(
        bankHolidays
          .map((item) => String(item?.date || "").slice(0, 10))
          .filter(Boolean),
      ),
    [bankHolidays],
  );

  const yearOptions = useMemo(
    () =>
      [year - 1, year, year + 1].map((item) => ({
        id: String(item),
        name: String(item),
      })),
    [year],
  );

  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }).map((_, index) => ({
        id: String(index),
        name: new Date(2026, index, 1).toLocaleString("en-GB", {
          month: "long",
        }),
      })),
    [],
  );

  const departmentOptions = useMemo(
    () => [
      { id: "all", name: "All Departments" },
      ...departments.map((department) => ({
        id: String(department.id),
        name: department.name,
      })),
    ],
    [departments],
  );

  const selectedDepartmentLabel = useMemo(() => {
    if (departmentName) return departmentName;

    return (
      departmentOptions.find(
        (department) => department.id === trackerDepartmentId,
      )?.name || "All Departments"
    );
  }, [departmentName, departmentOptions, trackerDepartmentId]);

  const legendItems = useMemo(() => {
    const allItems = [
      {
        id: "annual",
        className: "staff-holiday-legend-pill--annual",
        code: "AL",
        label: "Annual Leave",
      },
      {
        id: "sick",
        className: "staff-holiday-legend-pill--sick",
        code: "S",
        label: "Sick Leave",
        adminOnly: true,
      },
      {
        id: "unpaid",
        className: "staff-holiday-legend-pill--unpaid",
        code: "UL",
        label: "Unpaid Leave",
      },
      {
        id: "paid",
        className: "staff-holiday-legend-pill--paid",
        code: "PL",
        label: "Paid Leave",
        adminOnly: true,
      },
      {
        id: "half",
        className: "staff-holiday-legend-pill--half",
        code: "0.5",
        label: "Half Day",
      },
      {
        id: "bank-holiday",
        className: "staff-holiday-legend-pill--bank-holiday",
        code: "BH",
        label: "Bank Holiday",
      },
    ];

    return allItems.filter((item) => !item.adminOnly || isHolidayAdmin);
  }, [isHolidayAdmin]);

  useEffect(() => {
    if (loading || visibleStaffRows.length === 0) return undefined;

    const syncTrackerRowHeights = () => {
      const staffHeaderRow = staffHeaderRowRef.current;
      const daysHeaderRow = daysHeaderRowRef.current;
      const staffRowsElements = Array.from(staffBodyRef.current?.rows || []);
      const dayRowsElements = Array.from(daysBodyRef.current?.rows || []);
      const rowCount = Math.min(
        staffRowsElements.length,
        dayRowsElements.length,
      );

      if (!staffHeaderRow || !daysHeaderRow || rowCount === 0) return;

      staffHeaderRow.style.height = "auto";
      daysHeaderRow.style.height = "auto";

      const headerHeight = Math.max(
        Math.ceil(staffHeaderRow.getBoundingClientRect().height),
        Math.ceil(daysHeaderRow.getBoundingClientRect().height),
      );

      staffHeaderRow.style.height = `${headerHeight}px`;
      daysHeaderRow.style.height = `${headerHeight}px`;

      for (let index = 0; index < rowCount; index += 1) {
        const staffRow = staffRowsElements[index];
        const dayRow = dayRowsElements[index];

        staffRow.style.height = "auto";
        dayRow.style.height = "auto";

        const rowHeight = Math.max(
          Math.ceil(staffRow.getBoundingClientRect().height),
          Math.ceil(dayRow.getBoundingClientRect().height),
        );

        staffRow.style.height = `${rowHeight}px`;
        dayRow.style.height = `${rowHeight}px`;
      }
    };

    syncTrackerRowHeights();
    window.addEventListener("resize", syncTrackerRowHeights);

    return () => {
      window.removeEventListener("resize", syncTrackerRowHeights);
    };
  }, [loading, visibleStaffRows, monthDates]);

  return (
    <div
      className={`staff-holiday-tracker-panel ${
        compact ? "staff-holiday-tracker-panel--compact" : ""
      }`}
    >
      <div className="staff-holiday-tracker-panel-header">
        {/* <div>
          <div className="FP_Content_Semibold">{selectedDepartmentLabel}</div>
          <p className="FP_Help_Text grey">
            Department availability for the selected request month.
          </p>
        </div> */}

        <div className="staff-holiday-filter-row">
          <CustomSelect
            options={yearOptions}
            value={String(year)}
            onChange={(event) => setYear(Number(event.target.value))}
            placeholder="Select year"
            clearable={false}
            searchable={false}
          />

          <CustomSelect
            options={monthOptions}
            value={String(monthIndex)}
            onChange={(event) => setMonthIndex(Number(event.target.value))}
            placeholder="Select month"
            clearable={false}
            searchable={false}
          />

          {showDepartmentFilter && (
            <CustomSelect
              options={departmentOptions}
              value={trackerDepartmentId}
              onChange={(event) => setSelectedDepartmentId(event.target.value)}
              placeholder="Select department"
              clearable={false}
              searchable
              searchPlaceholder="Search departments"
            />
          )}
        </div>
      </div>

      <div className="staff-holiday-legend">
        {legendItems.map((item) => (
          <span
            key={item.id}
            className={`staff-holiday-legend-pill ${item.className}`}
          >
            <b>{item.code}</b> {item.label}
          </span>
        ))}
      </div>

      <div className="staff-holiday-card staff-holiday-tracker-card">
        {loading ? (
          <p>Loading tracker...</p>
        ) : visibleStaffRows.length === 0 ? (
          <p className="staff-holiday-tracker-empty">
            No staff or leave found for this department.
          </p>
        ) : (
          <div className="staff-holiday-tracker-layout">
            <div className="staff-holiday-tracker-fixed-column">
              <table className="staff-holiday-tracker-table staff-holiday-tracker-table--kanban staff-holiday-tracker-table--staff-only">
                <thead>
                  <tr ref={staffHeaderRowRef}>
                    <th className="staff-holiday-staff-head">Staff</th>
                  </tr>
                </thead>

                <tbody ref={staffBodyRef}>
                  {visibleStaffRows.map((row) => {
                    const isSelectedStaff =
                      highlightedStaffEmail &&
                      normaliseEmail(row.staffEmail) === highlightedStaffEmail;

                    return (
                      <tr key={row.staffEmail}>
                        <td
                          className={`staff-holiday-staff-cell ${
                            isSelectedStaff
                              ? "staff-holiday-staff-cell--selected-request"
                              : ""
                          }`}
                        >
                          <strong>{row.staffName}</strong>
                          <span>{row.departmentName || "No Department"}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="staff-holiday-tracker-scroll staff-holiday-tracker-scroll--kanban">
              <table className="staff-holiday-tracker-table staff-holiday-tracker-table--kanban staff-holiday-tracker-table--days-only">
                <thead>
                  <tr ref={daysHeaderRowRef}>
                    {monthDates.map((date) => {
                      const dateKey = toDateKey(date);
                      const isBankHoliday = bankHolidayDateKeys.has(dateKey);
                      const isToday = dateKey === todayDateKey;
                      const isSelectedRequestDate =
                        isHighlightedRequestDate(dateKey);

                      return (
                        <th
                          key={dateKey}
                          className={`staff-holiday-day-head ${
                            isBankHoliday
                              ? "staff-holiday-day-head--bank-holiday"
                              : ""
                          } ${isToday ? "staff-holiday-day-head--today" : ""} ${
                            isSelectedRequestDate
                              ? "staff-holiday-day-head--selected-request"
                              : ""
                          }`}
                        >
                          <span>
                            {date.toLocaleDateString("en-GB", {
                              weekday: "short",
                            })}
                          </span>
                          <strong>{date.getDate()}</strong>
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                <tbody ref={daysBodyRef}>
                  {visibleStaffRows.map((row) => {
                    const eventMap = new Map(
                      row.events.map((event) => [event.date, event]),
                    );
                    const isSelectedStaff =
                      highlightedStaffEmail &&
                      normaliseEmail(row.staffEmail) === highlightedStaffEmail;

                    return (
                      <tr key={row.staffEmail}>
                        {monthDates.map((date) => {
                          const dateKey = toDateKey(date);
                          const event = eventMap.get(dateKey);
                          const isBankHoliday =
                            bankHolidayDateKeys.has(dateKey);
                          const isToday = dateKey === todayDateKey;
                          const isSelectedRequestCell =
                            isSelectedStaff &&
                            isHighlightedRequestDate(dateKey);

                          const selectedRequestDayValue = isSelectedRequestCell
                            ? getHighlightedDayValue(dateKey)
                            : 1;

                          const displayedLeaveType =
                            event?.leaveType || highlightLeaveType;

                          const displayedDayValue =
                            event?.dayValue ?? selectedRequestDayValue;

                          return (
                            <td
                              key={dateKey}
                              className={`FP_Body_Semibold staff-holiday-day-cell ${
                                isBankHoliday
                                  ? "staff-holiday-day-cell--bank-holiday"
                                  : ""
                              } ${
                                isToday ? "staff-holiday-day-cell--today" : ""
                              } ${
                                event ? `leave-cell ${event.leaveType}` : ""
                              } ${
                                isSelectedRequestCell
                                  ? `staff-holiday-day-cell--selected-request ${
                                      !event && highlightLeaveType
                                        ? `leave-cell ${highlightLeaveType}`
                                        : ""
                                    }`
                                  : ""
                              }`}
                            >
                              {event || isSelectedRequestCell
                                ? getLeaveCodeForTracker(
                                    displayedLeaveType,
                                    displayedDayValue,
                                  )
                                : ""}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffHolidayTrackerPanel;
